/******************************************************
 * Tag Design Groups
 * Version: 1.0.0
 *
 * Splits the "Tag Design" swatch picker on the engraved name-tag PDP into
 * labelled groups — "Standard Tags", then "Halloween Tag Designs" — without
 * changing what MageWorx submits.
 *
 * WHY IT WORKS THE WAY IT DOES (read before editing):
 *
 * 1. MageWorx APO cannot do this itself. Its `group_id` groups whole OPTIONS,
 *    not values inside one option, and all 33 designs are values of a single
 *    swatch option (o_2115925). So the split has to happen in the theme.
 *
 * 2. The trigger is the WORD "halloween" in the value title, set by the owner
 *    in the MageWorx admin ("Halloween Pumpkin", "Halloween Ghost", ...).
 *    Nothing here rewrites or strips the title. That is deliberate: MageWorx
 *    submits the value TITLE as the line-item property, so the title reaches
 *    the cart, the order, the confirmation email and the packing slip that the
 *    engraver reads. It also rebuilds its own "accumulated title" readout from
 *    internal state on every change, so any title we edited in the DOM would be
 *    overwritten on the next click anyway.
 *
 * 3. Swatch nodes are MOVED (appendChild / insertBefore) and never cloned,
 *    re-serialised, or written through innerHTML. Both MageWorx and
 *    tag-preview.js bind listeners DIRECTLY on each radio node rather than
 *    delegating. Moving preserves those listeners; re-serialising silently
 *    destroys them — the swatches keep looking clickable, the live preview
 *    stops updating, and MageWorx never records a Tag Design at all.
 *
 * 4. Everything stays inside the ORIGINAL single `.mw-option__container`.
 *    - MageWorx's own deselect logic uses `.mw-option__container > .mw-option__value--selected`,
 *      so a moved node must remain a DIRECT CHILD of that container or two
 *      swatches end up highlighted at once.
 *    - Its click handler walks `closest('[data-option-id]')`, so a moved node
 *      must remain a descendant of #o_2115925 or the click is swallowed.
 *    - Several MageWorx code paths (show-first/expand, choicetile, swiper) use
 *      a SINGULAR querySelector for `.mw-option__container`. Those are inert at
 *      today's settings (value_visibility "showall", swatches_position
 *      "horizontal") but would silently ignore a second container if the owner
 *      ever switches them on. One container avoids that whole class of bug.
 *
 * 5. If no value matches, this does nothing at all and the page renders exactly
 *    as it did before. That is what makes it safe to ship ahead of the artwork.
 *
 * Optional overrides, if `window.HD_TAG_GROUPS` is defined before this runs:
 *   { optionId, match, standardHeading, groupHeading }
 ******************************************************/

(function () {
  'use strict';

  var DEFAULTS = {
    optionId: 'o_2115925',
    match: 'halloween',
    standardHeading: 'Standard Tags',
    groupHeading: 'Halloween Tag Designs'
  };

  // WRONG ASSUMPTION, corrected 2026-08-06: MageWorx does NOT render into
  // `.mw-product-options`. Verified in a real browser against live theme
  // 164252942571 — that element holds 1 child and 141 characters, while the
  // options render into `div.mw-apo-container` (id `options_<timestamp>`) under
  // `form.product-form`, a different branch entirely:
  //
  //   OPTION_INSIDE_MOUNT: false
  //   #o_2115925 < div#options_1786078641875.mw-apo-container < form.product-form
  //
  // So an observer on the mount never saw APO draw the swatches, and grouping
  // only applied when some unrelated mutation happened to touch the mount. That
  // is a race: it won in Firefox and lost every time in headless Chrome, which
  // means a share of shoppers silently got 48 ungrouped swatches.
  //
  // We now observe document.body. It is the one node that can never be detached
  // or replaced — the previous two targets both turned out to be neither — and
  // `.mw-apo-container`'s id is regenerated per render, so it is not a safe
  // target either. The cost is a noisy observer, paid for by only counting
  // productive passes against MAX_RUNS (see safeApply).
  var MOUNT_SELECTOR = 'body';

  var HEADING_ATTR = 'data-hd-tag-heading';
  var GROUPED_ATTR = 'data-hd-grouped';
  var MAX_RUNS = 50;

  var config = {};
  var runs = 0;
  var applying = false;
  var observer = null;

  function readConfig() {
    var overrides = window.HD_TAG_GROUPS || {};
    var out = {};
    for (var k in DEFAULTS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULTS, k)) {
        var v = overrides[k];
        out[k] = (typeof v === 'string' && v.trim() !== '') ? v.trim() : DEFAULTS[k];
      }
    }
    return out;
  }

  function injectStyles() {
    if (document.getElementById('hd-tag-group-styles')) return;
    var style = document.createElement('style');
    style.id = 'hd-tag-group-styles';
    // Scoped to the Tag Design option only. The width override is required:
    // MageWorx sets `width: max-content` on .mw-option__container, against
    // which a full-width flex item cannot resolve reliably.
    style.textContent =
      '#' + config.optionId + ' .mw-option__container[' + GROUPED_ATTR + '] {' +
      '  width: 100%;' +
      '}' +
      '#' + config.optionId + ' [' + HEADING_ATTR + '] {' +
      '  flex: 0 0 100%;' +
      '  width: 100%;' +
      '  box-sizing: border-box;' +
      '  margin: 18px 0 2px 10px;' +
      '  font-size: 14px;' +
      '  font-weight: 600;' +
      '  line-height: 1.3;' +
      '  letter-spacing: 0.02em;' +
      '  padding-bottom: 6px;' +
      '  border-bottom: 1px solid rgba(18, 18, 18, 0.12);' +
      '}' +
      '#' + config.optionId + ' [' + HEADING_ATTR + ']:first-child {' +
      '  margin-top: 4px;' +
      '}';
    document.head.appendChild(style);
  }

  function makeHeading(text) {
    var el = document.createElement('div');
    el.setAttribute(HEADING_ATTR, '');
    el.className = 'hd-tag-group-heading';
    // The grouping is already conveyed to assistive tech by the value titles
    // themselves ("Halloween Pumpkin"), and these headings sit inside a radio
    // group where a stray heading would only add noise.
    el.setAttribute('aria-hidden', 'true');
    el.textContent = text;
    return el;
  }

  // Title comes from the swatch image's alt text, which MageWorx renders from
  // the value title. Falls back to the tooltip/text node for non-image swatches.
  function valueTitle(node) {
    var img = node.querySelector('img.mw-option__swatch-image');
    if (img && img.getAttribute('alt')) return img.getAttribute('alt');
    var text = node.querySelector('.mw-option__value-text');
    if (text && text.textContent) return text.textContent;
    return '';
  }

  function isMatch(node) {
    var title = valueTitle(node).trim().toLowerCase();
    return title !== '' && title.indexOf(config.match.toLowerCase()) !== -1;
  }

  // Returns true only when this pass actually changed the DOM. safeApply uses
  // that to decide whether the pass counts against MAX_RUNS.
  function apply() {
    var option = document.getElementById(config.optionId);
    if (!option) return false;

    var container = option.querySelector('.mw-option__container');
    if (!container) return false;

    // Scope to DIRECT children with a non-empty value id. An unscoped
    // `.mw-option__value[data-value-id]` also matches select-type wrappers,
    // which carry an empty data-value-id.
    var values = [];
    for (var i = 0; i < container.children.length; i++) {
      var child = container.children[i];
      if (
        child.classList &&
        child.classList.contains('mw-option__value') &&
        child.getAttribute('data-value-id')
      ) {
        values.push(child);
      }
    }
    if (values.length === 0) return false;

    var standard = [];
    var grouped = [];
    for (var j = 0; j < values.length; j++) {
      (isMatch(values[j]) ? grouped : standard).push(values[j]);
    }

    // Drop any headings from a previous pass before deciding, so re-runs and
    // the "owner removed the last Halloween design" case both settle correctly.
    var stale = container.querySelectorAll('[' + HEADING_ATTR + ']');
    for (var s = 0; s < stale.length; s++) stale[s].parentNode.removeChild(stale[s]);

    if (grouped.length === 0) {
      // Nothing to group: leave the picker exactly as MageWorx rendered it.
      // Only a real teardown (stale headings actually removed) counts as work.
      var hadGrouping = container.hasAttribute(GROUPED_ATTR) || stale.length > 0;
      container.removeAttribute(GROUPED_ATTR);
      return hadGrouping;
    }

    // Set the guard BEFORE mutating so a mutation we cause can't be mistaken
    // for someone else's render.
    container.setAttribute(GROUPED_ATTR, '');

    // Re-append in the order we want. appendChild on an existing child MOVES
    // it — no clone, no re-serialisation, listeners intact.
    //
    // SEASONAL GROUP FIRST (owner request, 2026-08-06). The seasonal designs are
    // the reason a shopper is on this page during the season, and the standard
    // set is long enough that they were below the fold. Note this also demotes
    // "Plain (No Design)", which MageWorx renders first and which was previously
    // the first swatch on the page — deliberate, and the thing to revert first
    // if plain-tag orders drop off.
    container.appendChild(makeHeading(config.groupHeading));
    for (var b = 0; b < grouped.length; b++) container.appendChild(grouped[b]);
    container.appendChild(makeHeading(config.standardHeading));
    for (var a = 0; a < standard.length; a++) container.appendChild(standard[a]);
    return true;
  }

  function safeApply() {
    // Re-entrancy guard: our own DOM writes fire the observer synchronously
    // in microtask order, and an unguarded re-entry is an infinite loop on the
    // one page this feature exists to improve.
    if (applying) return;
    if (runs >= MAX_RUNS) return;
    applying = true;
    if (observer) observer.disconnect();
    try {
      // Count only passes that actually changed something. Previously every
      // call incremented, including the no-ops before MageWorx has rendered
      // anything — with a body-wide observer that would burn the whole budget
      // during page load and permanently disable grouping before the swatches
      // even exist. MAX_RUNS is an infinite-loop backstop, not a page-load one;
      // re-entrancy is guarded separately by `applying`.
      if (apply()) runs++;
    } catch (err) {
      // Fail soft: a broken grouping must never take the picker down with it.
      if (window.console && console.warn) {
        console.warn('[tag-design-groups] skipped:', err);
      }
    } finally {
      if (observer) observeMount();
      applying = false;
    }
  }

  function observeMount() {
    var mount = document.querySelector(MOUNT_SELECTOR) || document.body;
    observer.observe(mount, { childList: true, subtree: true });
  }

  function start() {
    config = readConfig();
    injectStyles();
    observer = new MutationObserver(function () {
      safeApply();
    });
    observeMount();
    safeApply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Exposed for the harness. Not part of the storefront contract.
  window.HDTagDesignGroups = {
    _apply: safeApply,
    _config: function () { return config; },
    _resetRuns: function () { runs = 0; }
  };
})();
